<template>
  <div v-loading="loading" class="op-area-hour-panel">
    <p class="mu-legend">
      有效供给是当时在场的车，加上这个半点之前已经收工的。缺口是库存目标比有效供给还多出来的车数。
      发车是本线这个小时发出去的车，发车差 = 发车 − 还要覆盖的需求车数。
    </p>
    <div ref="tableWrap" class="table-wrap">
      <el-table :data="rows" :max-height="bodyHeight" class="no-hover" stripe empty-text="暂无运营层数据，请先选择路线并完成 EV 推导">
        <el-table-column label="时段" width="130">
          <template slot-scope="scope">
            {{ slotLabel(scope.row) }}
          </template>
        </el-table-column>

        <el-table-column label="有效供给" prop="supplyOpEffective" width="88">
          <template slot="header">
            <el-tooltip content="这条线这个半点还能算上的车：当时在场的，加上这个半点之前已经收工的" placement="top">
              <span>有效供给</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="需求" prop="demandCars" width="56">
          <template slot="header">
            <el-tooltip content="这个小时还要覆盖的需求车数。已锁进班次的座位先扣掉，再按每车座位向上取整。只写在整点，半点这格是 0" placement="top">
              <span>需求</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column label="库存需求" prop="inventoryTarget" width="80">
          <template slot="header">
            <el-tooltip content="这条线这个半点的库存目标，营业时间内最少 1 车。订单热度用整点那一档，缺口和多出的车用本半点" placement="top">
              <span>库存需求</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="缺口" width="56">
          <template slot="header">
            <el-tooltip content="库存目标比有效供给还多出来的车数。有效供给含这个半点之前已经收工的车" placement="top">
              <span>缺口</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <span :class="{ shortage: shortage(scope.row) > 0 }">
              {{ raw(shortage(scope.row)) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="多出" width="64">
          <template slot="header">
            <el-tooltip content="有效供给比库存目标多几辆。负数表示不够。剩车打折按这个数算" placement="top">
              <span>多出</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <span :class="{ shortage: leftover(scope.row) != null && leftover(scope.row) < 0 }">
              {{ raw(leftover(scope.row)) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="发车" width="60" class-name="w-col">
          <template slot="header">
            <el-tooltip content="这条线这个小时发出去的载客车。只写在整点，半点这格是 0" placement="top">
              <span>发车</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ raw(scope.row.departCars) }}
          </template>
        </el-table-column>
        <el-table-column label="发车差" width="72" class-name="w-col">
          <template slot="header">
            <el-tooltip content="发出去的车减去这个小时还要覆盖的需求车数。正数是多发，负数是没盖住" placement="top">
              <span>发车差</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <span :class="{ shortage: departLeftover(scope.row) != null && departLeftover(scope.row) < 0 }">
              {{ raw(departLeftover(scope.row)) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column v-if="!missingMuFields" label="已订座位" width="88">
          <template slot="header">
            <el-tooltip content="这个整点这条线已经订上的座位，含已派。两个半点是同一个数。订单热度还会和库存目标比，取更大的那个再换成车" placement="top">
              <span>已订座位</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ raw(seats(scope.row)) }}
          </template>
        </el-table-column>
        <el-table-column v-if="!missingMuFields" label="订单热度" width="88" class-name="mu-col">
          <template slot="header">
            <el-tooltip content="订单热度。不看这次求解发了多少车。默认 2 车为 1，6 车封顶。同一小时的两个半点相同" placement="top">
              <span>订单热度</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(muDemand(scope.row)) }}
          </template>
        </el-table-column>
        <el-table-column v-if="!missingMuFields" label="折扣" width="64" class-name="mu-col">
          <template slot="header">
            <el-tooltip content="剩车打折。有效供给比库存目标多出的车不超过缓冲时为 1，车堆多了才变小" placement="top">
              <span>折扣</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(sigma(scope.row)) }}
          </template>
        </el-table-column>
        <el-table-column v-if="!missingMuFields" label="收工热度" width="88" class-name="mu-col">
          <template slot="header">
            <el-tooltip content="收工热度 = 订单热度 × 剩车折扣。收工积分按它计价" placement="top">
              <span>收工热度</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <el-tooltip :content="muTooltip(scope.row)" placement="top">
              <span>{{ formatNum(muFinal(scope.row)) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column v-if="!missingMuFields" label="区域权重" width="88" class-name="w-col">
          <template slot="header">
            <el-tooltip content="区域权重。这一天单独配过就用配置，否则用默认" placement="top">
              <span>区域权重</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(scope.row.wLoc) }}
          </template>
        </el-table-column>
        <el-table-column v-if="!missingMuFields" label="时段权重" width="88" class-name="w-col">
          <template slot="header">
            <el-tooltip content="这个小时的时段权重" placement="top">
              <span>时段权重</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(scope.row.wTime) }}
          </template>
        </el-table-column>
        <el-table-column v-if="!missingMuFields" label="日期权重" width="88" class-name="w-col">
          <template slot="header">
            <el-tooltip content="这一天的日期权重。指定过这一天就用指定值，否则按工作日、周末、节假日" placement="top">
              <span>日期权重</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(scope.row.wDate) }}
          </template>
        </el-table-column>
        <el-table-column label="本档收工" width="80">
          <template slot="header">
            <el-tooltip content="这个半点收工的车数。和区域总数不一样时，显示更大的那个" placement="top">
              <span>本档收工</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <span :class="{ 'stop-work-hit': displayStopZ(scope.row) > 0 }">
              {{ formatStopZ(scope.row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column v-if="!missingMuFields" label="积分" width="64">
          <template slot="header">
            <el-tooltip content="这个半点收工带回来的有效积分" placement="top">
              <span>积分</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ raw(scope.row.scoreTotal) }}
          </template>
        </el-table-column>

        <el-table-column label="此前收工" prop="stopWorkCumPrev" width="88">
          <template slot="header">
            <el-tooltip content="这个半点之前已经收工的车，算进有效供给" placement="top">
              <span>此前收工</span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
/** 运营层诊断：主表看供给、库存和缺口。旧结果仍带热度时才展开分档。 */
export default {
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    tableHeight: {
      type: Number,
      default: 480
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      wrapHeight: 0
    };
  },

  computed: {
    bodyHeight() {
      return this.wrapHeight || this.tableHeight;
    },
    missingMuFields() {
      const list = this.rows || [];
      if (!list.length) {
        return false;
      }
      return list.every((row) => row && row.muDemandSeats == null && row.muDemand == null && row.sigma == null);
    }
  },

  watch: {
    missingMuFields() {
      this.$nextTick(() => this.syncWrapHeight());
    }
  },

  mounted() {
    this.$nextTick(() => this.bindWrapObserver());
  },

  beforeDestroy() {
    this.unbindWrapObserver();
  },

  methods: {
    bindWrapObserver() {
      this.syncWrapHeight();
      const wrap = this.$refs.tableWrap;
      if (!wrap || typeof ResizeObserver === "undefined") {
        return;
      }
      this._wrapObserver = new ResizeObserver(() => {
        this.syncWrapHeight();
      });
      this._wrapObserver.observe(wrap);
    },
    unbindWrapObserver() {
      if (this._wrapObserver) {
        this._wrapObserver.disconnect();
        this._wrapObserver = null;
      }
    },
    syncWrapHeight() {
      const wrap = this.$refs.tableWrap;
      if (!wrap) {
        return;
      }
      const h = Math.floor(wrap.clientHeight);
      if (h > 0 && h !== this.wrapHeight) {
        this.wrapHeight = h;
      }
    },
    raw(val) {
      return val === null || val === undefined ? "-" : val;
    },
    slotLabel(row) {
      if (!row) {
        return "-";
      }
      if (row.slotRange) {
        return row.slotRange;
      }
      const slot = Number(row.hour);
      if (!Number.isFinite(slot)) {
        return "-";
      }
      const pad = (n) => String(n).padStart(2, "0");
      const start = slot * 30;
      const end = start + 30;
      const text = (minutes) => pad(Math.floor(minutes / 60) % 24) + ":" + pad(minutes % 60);
      return text(start) + "-" + text(end);
    },
    formatNum(val) {
      if (val === null || val === undefined) {
        return "-";
      }
      return this.$options.filters.numFormat(val, 2);
    },
    selectedBin(row) {
      const bins = row && row.bins;
      if (!bins || !bins.length) {
        return null;
      }
      return bins.find((b) => b && b.selected) || null;
    },
    leftover(row) {
      if (!row) {
        return null;
      }
      if (row.surplusOpMu != null) {
        return row.surplusOpMu;
      }
      if (row.supplyOpEffective != null && row.inventoryTarget != null) {
        return row.supplyOpEffective - row.inventoryTarget;
      }
      return null;
    },
    shortage(row) {
      if (!row) {
        return null;
      }
      if (row.inventoryShortage != null) {
        return row.inventoryShortage;
      }
      const x = this.leftover(row);
      return x == null ? null : Math.max(0, -x);
    },
    departLeftover(row) {
      if (!row) {
        return null;
      }
      if (row.departSurplus != null) {
        return row.departSurplus;
      }
      if (row.departCars != null && row.demandCars != null) {
        return row.departCars - row.demandCars;
      }
      return null;
    },
    seats(row) {
      return row.muDemandSeats;
    },
    muDemand(row) {
      if (row.muDemand != null) {
        return row.muDemand;
      }
      const bin = this.selectedBin(row);
      if (bin && bin.sigma && bin.mu != null) {
        return bin.mu / bin.sigma;
      }
      return null;
    },
    sigma(row) {
      if (row.sigma != null) {
        return row.sigma;
      }
      const bin = this.selectedBin(row);
      return bin && bin.sigma != null ? bin.sigma : null;
    },
    muFinal(row) {
      if (row.mu != null) {
        return row.mu;
      }
      const md = this.muDemand(row);
      const sg = this.sigma(row);
      if (md != null && sg != null) {
        return md * sg;
      }
      const bin = this.selectedBin(row);
      return bin && bin.sigma != null && bin.mu != null ? bin.mu : null;
    },
    muTooltip(row) {
      return `订单热度 × 折扣 = ${this.formatNum(this.muDemand(row))} × ${this.formatNum(this.sigma(row))}`;
    },
    binRowClass({ row }) {
      return row.selected ? "bin-selected-row" : "";
    },
    displayStopZ(row) {
      const pathZ = row.stopWorkCount == null ? 0 : row.stopWorkCount;
      const globalZ = row.globalStopWorkCount == null ? pathZ : row.globalStopWorkCount;
      return Math.max(pathZ, globalZ);
    },
    formatStopZ(row) {
      const pathZ = row.stopWorkCount;
      const globalZ = row.globalStopWorkCount;
      if (pathZ == null && globalZ == null) {
        return "-";
      }
      if (globalZ != null && pathZ != null && globalZ !== pathZ) {
        return `${pathZ} (全局${globalZ})`;
      }
      return this.raw(pathZ != null ? pathZ : globalZ);
    }
  }
};
</script>

<style lang="less" scoped>
.op-area-hour-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.mu-legend {
  flex-shrink: 0;
  margin: 0 0 8px;
  color: #c0c4cc;
  font-size: 12px;
  line-height: 1.4;
}

.mu-stale {
  flex-shrink: 0;
  margin: -4px 0 8px;
  color: #e6a23c;
  font-size: 12px;
  line-height: 1.4;
}

.bin-empty {
  padding: 8px 12px;
  color: #909399;
  font-size: 12px;
}

.shortage {
  color: #f56c6c;
  font-weight: 600;
}

.selected-tag {
  color: #409eff;
  font-weight: 600;
}

.stop-work-hit {
  color: #e6a23c;
  font-weight: 600;
}

::v-deep .mu-col {
  background-color: #f4f9ff;
}

::v-deep .w-col {
  background-color: #f8f4ff;
}

::v-deep .bin-selected-row {
  background-color: #ecf5ff !important;
}
</style>
